import React, { useContext } from 'react';
import {
  Button,
  Col,
  Row,
  Tag,
  theme as antTheme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import {
  bandColor,
  bandLabel,
  shortDate,
} from '@totem/components/accountPlan/utilities';
import colors from '@totem/styles/colors';

const pillStyle = { borderRadius: 999, fontWeight: 600, padding: '1px 10px' };

const Owner = ({
  label,
  name,
  fromPortal,
  tip,
}: {
  label: string;
  name: string;
  fromPortal?: boolean;
  tip?: string;
}) => {
  const { token } = antTheme.useToken();
  return (
    <div style={{ textAlign: 'right', lineHeight: 1.35 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {fromPortal && (
          <span
            style={{
              color: token.colorLink,
              fontSize: 10,
              fontWeight: 700,
              marginRight: 4,
            }}
          >
            FROM PORTAL
          </span>
        )}
        {label}
      </Typography.Text>
      <div>
        <Tooltip title={tip}>
          <Typography.Text strong>{name}</Typography.Text>
        </Tooltip>
      </div>
    </div>
  );
};

const AccountHeader = () => {
  const { data, openEditor } = useContext(AccountPlanContext);
  if (!data) {
    return null;
  }
  const { plan, summary } = data;
  const { organization, owners, health, revenue, tickets, vulnerabilities } =
    summary;

  const subLine = [
    organization.domain,
    `Customer since ${dayjs(organization.createdAt).format('MMM YYYY')}`,
    `${summary.buildings.underService} buildings under service (${summary.buildings.total} records)`,
    `${summary.deviceCount.toLocaleString()} asset records`,
  ]
    .filter(Boolean)
    .join(' · ');

  const champion = owners.customerChampion;

  const p1 = tickets.byPriority.P1 ?? 0;
  const alertParts = [
    p1 > 0 ? `${p1} P1` : '',
    vulnerabilities.confirmedOpen >= 100
      ? `${vulnerabilities.confirmedOpen} CVEs`
      : '',
  ].filter(Boolean);

  return (
    <Row gutter={24} wrap>
      <Col flex="auto">
        <Typography.Title level={2} style={{ margin: 0, fontSize: 26 }}>
          {organization.name}
        </Typography.Title>
        <Typography.Text type="secondary">{subLine}</Typography.Text>
        {plan.profile.oneLiner && (
          <div>
            <Typography.Text italic>{plan.profile.oneLiner}</Typography.Text>
          </div>
        )}
        <div
          style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}
        >
          {plan.profile.tier && (
            <Tag color={colors.branding.primaryDark} style={pillStyle}>
              {plan.profile.tier}
            </Tag>
          )}
          {(plan.profile.segment || plan.profile.region) && (
            <Tag style={pillStyle}>
              {[
                plan.profile.segment,
                plan.profile.region && `(${plan.profile.region})`,
              ]
                .filter(Boolean)
                .join(' ')}
            </Tag>
          )}
          <Tag color={bandColor(health.band)} style={pillStyle}>
            Health {health.score} — {bandLabel(health.band)}
          </Tag>
          {alertParts.length > 0 && (
            <Tag color="red" style={pillStyle}>
              {alertParts.join(' · ')}
            </Tag>
          )}
          {revenue.nextRenewal && (
            <Tag
              color={
                revenue.nextRenewal.action === 'negotiate' ? 'gold' : 'default'
              }
              style={pillStyle}
            >
              Renewal {shortDate(revenue.nextRenewal.date)}
            </Tag>
          )}
        </div>
        <div style={{ marginTop: 10 }}>
          <Button size="small" onClick={() => openEditor({ section: 'setup' })}>
            Account setup ✎
          </Button>
          <Typography.Text
            type="secondary"
            style={{ marginLeft: 12, fontSize: 12 }}
          >
            As of {dayjs(summary.asOf).format('MMM D, YYYY h:mm A')}
          </Typography.Text>
        </div>
      </Col>
      <Col flex="none">
        <Owner
          label="Account Manager"
          name={owners.accountManager?.name ?? '—'}
          fromPortal
        />
        <Owner
          label="Sales Rep"
          name={`${owners.salesRep.name}${owners.salesRepIsDefault ? ' (default)' : ''}`}
          fromPortal={owners.salesRepSource === 'portal'}
          tip={
            {
              portal: undefined,
              netsuite:
                'From the most recent NetSuite opportunity — add a "Sales Rep" point of contact to override',
              default:
                'Default — add a "Sales Rep" point of contact to the organization to override',
            }[owners.salesRepSource]
          }
        />
        <Owner
          label="Engagement Mgr"
          name={owners.engagementManager?.name ?? '—'}
          fromPortal
        />
        <Owner
          label="Customer Champion"
          fromPortal={champion?.source === 'portal'}
          tip={
            champion?.source === 'portal'
              ? 'Highest-ranked customer contact in the Portal — confirm in the Stakeholder Map'
              : undefined
          }
          name={
            champion
              ? `${champion.name}${champion.role ? ` (${champion.role})` : ''}`
              : '—'
          }
        />
      </Col>
    </Row>
  );
};

export default AccountHeader;
